import { Alert, Box } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { TranslationDialog, TranslationFilters, TranslationsTable, translationModel } from 'entities/translation';

import { EEntityType, ETranslationStatus, TranslationEntityItem, TranslationFields } from 'shared/api';
import { useDebouncedSearch } from 'shared/lib';

type Props = {
  entityType: EEntityType;
  language: string;
  status: ETranslationStatus;
  filter: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  page: number;
  rowsPerPage: number;
  onEntityTypeChange: (type: EEntityType) => void;
  onLanguageChange: (lang: string) => void;
  onStatusChange: (status: ETranslationStatus) => void;
  onFilterChange: (value: string) => void;
  onSortChange: (columnId: string) => void;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
};

export const EditorSection = ({
  entityType,
  language,
  status,
  filter,
  sortBy,
  sortOrder,
  page,
  rowsPerPage,
  onEntityTypeChange,
  onLanguageChange,
  onStatusChange,
  onFilterChange,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
}: Props) => {
  const dispatch = useDispatch();
  const { languages, entities, pagination } = translationModel.useTranslationState();
  const { mutate: upsert, isLoading: isSaving } = translationModel.upsertTranslationAsync();

  const [editItem, setEditItem] = useState<TranslationEntityItem | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const debouncedFilter = useDebouncedSearch(filter, 250);

  const params = useMemo(
    () => ({
      language,
      status,
      filter: debouncedFilter || undefined,
      sortBy,
      sortOrder,
      page,
      limit: rowsPerPage,
    }),
    [language, status, debouncedFilter, sortBy, sortOrder, page, rowsPerPage],
  );

  const { isFetching } = translationModel.getEntitiesAsync(entityType, params)(dispatch);

  const handleEdit = useCallback((item: TranslationEntityItem) => {
    setEditItem(item);
    setIsDialogOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    if (!isSaving) {
      setIsDialogOpen(false);
      setEditItem(null);
    }
  }, [isSaving]);

  const handleSubmit = useCallback(
    (fields: TranslationFields) => {
      if (!editItem) return;
      upsert(
        {
          entityType,
          entityId: editItem.entityId,
          language,
          fields,
        },
        {
          onSuccess: () => {
            setIsDialogOpen(false);
            setEditItem(null);
          },
        },
      );
    },
    [editItem, entityType, language, upsert],
  );

  const languageMeta = languages.find((l) => l.code === language) ?? null;
  const noLanguages = languages.length === 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {noLanguages && (
        <Alert severity="info" sx={{ mb: 1 }}>
          No languages have translations yet. The Coverage tab shows targets; the backend or an import must seed the
          first record.
        </Alert>
      )}

      <TranslationFilters
        entityType={entityType}
        language={language}
        status={status}
        filter={filter}
        languages={languages}
        totalCount={pagination?.total ?? 0}
        onEntityTypeChange={onEntityTypeChange}
        onLanguageChange={onLanguageChange}
        onStatusChange={onStatusChange}
        onFilterChange={onFilterChange}
      />

      <TranslationsTable
        items={entities}
        pagination={pagination}
        page={page}
        rowsPerPage={rowsPerPage}
        sortBy={sortBy}
        sortOrder={sortOrder}
        isLoading={isFetching}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        onSortChange={onSortChange}
        onEdit={handleEdit}
      />

      <TranslationDialog
        isOpen={isDialogOpen}
        entityType={entityType}
        language={languageMeta}
        item={editItem}
        isSaving={isSaving}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />
    </Box>
  );
};
